import { Minus, TrendingDown, TrendingUp } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import type { LeaderboardTrend as Trend } from "@/types/leaderboard"

/**
 * OKS-394 — per-row trend indicator (▲/▼/—).
 *
 * Renders ONE fixed-width cell carrying the direction a member moved
 * versus the prior instance of the selected period (UX Spec §2). Shared
 * by `leaderboard-row.tsx` + `leaderboard-self-pin.tsx` so the
 * glyph/colour/label mapping lives in a single place.
 *
 * # Mapping (UX Spec §2)
 *
 *   - "up"   → `TrendingUp`,   green  (`text-emerald-600`)
 *   - "down" → `TrendingDown`, red    (`text-red-600`)
 *   - "dash" → `Minus`,        muted  (`text-muted-foreground`)
 *
 * # Accessibility (UX Spec §6 — colour is NOT the sole signal)
 *
 * Meaning is carried by BOTH the distinct glyph shape AND a text label:
 * the wrapper `<span>` carries an `aria-label` (e.g. "Trending up") and
 * a matching native `title`; the glyph itself is `aria-hidden` so the SR
 * announces the label once, not the icon name. The cell is non-
 * interactive (display only) — no focus, no touch-target rule.
 *
 * Layout: `w-5 shrink-0` so rows stay vertically aligned regardless of
 * which of the three glyphs renders, and the cell never gets pushed off
 * by a long (flex-1, truncating) display name.
 */
interface LeaderboardTrendProps {
  trend: Trend
  className?: string
}

const TREND_CONFIG: Record<
  Trend,
  { Icon: typeof TrendingUp; colorClass: string; labelKey: string }
> = {
  up: {
    Icon: TrendingUp,
    colorClass: "text-emerald-600",
    labelKey: "leaderboard.trend.up",
  },
  down: {
    Icon: TrendingDown,
    colorClass: "text-red-600",
    labelKey: "leaderboard.trend.down",
  },
  dash: {
    Icon: Minus,
    colorClass: "text-muted-foreground",
    labelKey: "leaderboard.trend.none",
  },
}

export function LeaderboardTrend({ trend, className }: LeaderboardTrendProps) {
  const { t } = useTranslation()
  const { Icon, colorClass, labelKey } = TREND_CONFIG[trend]
  const label = t(labelKey)

  return (
    <span
      data-testid="leaderboard-trend"
      data-trend={trend}
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        "flex w-5 shrink-0 items-center justify-center",
        colorClass,
        className,
      )}
    >
      <Icon className="size-4" aria-hidden="true" />
    </span>
  )
}
